import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Client } from '@twilio/conversations';
import SupportService from '../services/support/supportService.ts';
import * as Sentry from '@sentry/react';

const supportService = new SupportService();

type SupportNotificationContextType = {
    hasUnread: boolean;
    markAllRead: () => void;
};

const SupportNotificationContext = createContext<SupportNotificationContextType>({
    hasUnread: false,
    markAllRead: () => { },
});

export const useSupportNotification = () => useContext(SupportNotificationContext);

export const SupportNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [hasUnread, setHasUnread] = useState(false);
    const clientRef = useRef<Client | null>(null);
    const subscribedRefs = useRef<Set<string>>(new Set());

    const isEligible = () =>
        localStorage.getItem('auth') === 'true'
        &&
        localStorage.getItem('ra_role') !== 'VENDOR';

    const initClient = useCallback(async () => {
        if (!isEligible()) return;

        try {
            const token = await supportService.getTwilioToken();
            const parsedToken = typeof token === 'string' ? token : token.token ?? token;

            const client = new Client(parsedToken);
            clientRef.current = client;

            client.on('connectionError', (error) => {
                console.error('🔴 [Support-Global] Błąd połączenia Twilio:', error);
            });

            client.on('messageAdded', (message) => {
                console.log('🌍 [Support-Global] NOWA WIADOMOŚĆ (globalny event):', message.sid, 'od:', message.author);
            });

            client.on('conversationUpdated', ({ conversation, updateReasons }) => {
                console.log(`🌍 [Support-Global] Konwersacja ${conversation.sid} zaktualizowana. Powód:`, updateReasons);
            });

            const subscribeToConversation = async (conversation: any) => {
                if (subscribedRefs.current.has(conversation.sid)) return;
                subscribedRefs.current.add(conversation.sid);

                console.log(`\n[Support] 🟢 Podpinam nasłuchiwanie do konwersacji: ${conversation.sid}`);

                console.log(`[Support] 📊 Status użytkownika w konwersacji: ${conversation.status}`);
                console.log(`[Support] 📊 Aktualny stan synchronizacji:`, conversation.syncState);

                if (conversation.status !== 'joined') {
                    console.warn(`[Support] ⚠️ Użytkownik nie ma statusu 'joined' w konwersacji ${conversation.sid}! Status to: ${conversation.status}`);
                }

                conversation.on('messageAdded', (message: any) => {
                    console.log('\n🔔 [Support-Lokalnie] NOWA WIADOMOŚĆ!');
                    console.log(`💬 Autor: ${message.author} | Treść: "${message.body}"`);
                    console.log(`📁 Konwersacja SID: ${message.conversation.sid}\n`);

                    if (message.author !== 'Support Team') {
                        setHasUnread(true);
                    }
                });

                conversation.on('updated', (update: any) => {
                    console.log(`[Support] 🔄 Konwersacja ${conversation.sid} otrzymała update:`, update.updateReasons);
                });

                conversation.on('typingStarted', (participant: any) => {
                    console.log(`[Support] ⌨️ Ktoś pisze w ${conversation.sid}:`, participant.identity);
                });
            };

            client.on('conversationAdded', (conversation) => {
                console.log(`[Support] Wykryto nową konwersację (conversationAdded): ${conversation.sid}`);
                subscribeToConversation(conversation);
            });

            client.on('stateChanged', async (state) => {
                console.log(`[Support] Stan klienta zmieniony na: ${state}`);

                if (state === 'initialized') {
                    console.log('[Support] Twilio Client gotowy! Pobieram konwersacje z serwera...');
                    try {
                        const paginator = await client.getSubscribedConversations();
                        console.log(`[Support] Znaleziono ${paginator.items.length} konwersacji. Podpinam eventy...`);

                        for (const conversation of paginator.items) {
                            await subscribeToConversation(conversation);
                        }
                    } catch (err) {
                        console.error('[Support] Błąd podczas pobierania konwersacji:', err);
                        Sentry.captureException(err);
                    }
                }
            });

            client.on('tokenAboutToExpire', async () => { /* ... */ });
            client.on('tokenExpired', async () => { /* ... */ });

        } catch (e) {
            console.error('[Support] Błąd inicjalizacji klienta Twilio:', e);
            Sentry.captureException(e);
        }
    }, []);

    const markAllRead = useCallback(() => setHasUnread(false), []);

    useEffect(() => {
        let isMounted = true;
        const setupClient = async () => {
            if (isMounted && !clientRef.current) await initClient();
        };
        setupClient();
        return () => {
            isMounted = false;
            if (clientRef.current) {
                clientRef.current.shutdown();
                clientRef.current = null;
                subscribedRefs.current.clear();
            }
        };
    }, [initClient]);

    return (
        <SupportNotificationContext.Provider value={{ hasUnread, markAllRead }}>
            {children}
        </SupportNotificationContext.Provider>
    );
};